import { BaseService, ServiceResponse } from './base.service';
import type { 
  HoleRecord, 
  CourseRecord, 
  PerformanceAchievement,
  RecordNotification
} from '../../types/performance-zones';

export interface RoundData {
  id: string;
  user_id: string;
  course_id: string;
  course_name: string;
  total_score: number;
  par_differential: number;
  total_par: number;
  completed_at: string;
  selected_tee_name?: string;
  hole_scores?: Array<{
    hole_number: number;
    strokes: number;
    par: number;
  }>;
}

export class PerformanceRecordsService extends BaseService {
  
  // Get all hole records held by a user
  async getHoleRecords(userId: string, filters?: {
    course_id?: string;
    record_type?: 'scratch' | 'net';
    gender?: 'male' | 'female' | 'overall';
  }): Promise<ServiceResponse<HoleRecord[]>> {
    try {
      let query = this.supabase
        .from('hole_records')
        .select(`
          *,
          courses!inner(name),
          profiles!inner(first_name, last_name, nickname)
        `)
        .eq('user_id', userId);

      if (filters?.course_id) {
        query = query.eq('course_id', filters.course_id);
      }
      if (filters?.record_type) {
        query = query.eq('record_type', filters.record_type);
      }
      if (filters?.gender) {
        query = query.eq('gender', filters.gender);
      }

      query = query.order('achieved_at', { ascending: false });

      const { data, error } = await query;

      if (error) throw error;

      // Transform data to include joined fields
      const transformedData: HoleRecord[] = data?.map(record => ({
        ...record,
        course_name: record.courses?.name,
        user_first_name: record.profiles?.first_name,
        user_last_name: record.profiles?.last_name,
        user_nickname: record.profiles?.nickname,
        user_name: record.profiles?.nickname || 
                  `${record.profiles?.first_name || ''} ${record.profiles?.last_name || ''}`.trim()
      })) || [];

      return this.handleResponse(transformedData);
    } catch (error) {
      return this.handleResponse(null, error);
    }
  }

  // Get all course records held by a user
  async getCourseRecords(userId: string, filters?: {
    course_id?: string;
    record_type?: 'scratch' | 'net';
    gender?: 'male' | 'female' | 'overall';
  }): Promise<ServiceResponse<CourseRecord[]>> {
    try {
      let query = this.supabase
        .from('course_records')
        .select(`
          *,
          courses!inner(name),
          profiles!inner(first_name, last_name, nickname)
        `)
        .eq('user_id', userId);

      if (filters?.course_id) {
        query = query.eq('course_id', filters.course_id);
      }
      if (filters?.record_type) {
        query = query.eq('record_type', filters.record_type);
      }
      if (filters?.gender) {
        query = query.eq('gender', filters.gender);
      }

      query = query.order('achieved_at', { ascending: false });

      const { data, error } = await query;

      if (error) throw error;

      // Transform data to include joined fields
      const transformedData: CourseRecord[] = data?.map(record => ({
        ...record,
        course_name: record.courses?.name,
        user_first_name: record.profiles?.first_name,
        user_last_name: record.profiles?.last_name,
        user_nickname: record.profiles?.nickname,
        user_name: record.profiles?.nickname || 
                  `${record.profiles?.first_name || ''} ${record.profiles?.last_name || ''}`.trim()
      })) || [];

      return this.handleResponse(transformedData);
    } catch (error) {
      return this.handleResponse(null, error);
    }
  }

  // Get achievement history for a user
  async getRecordHistory(userId: string, limit = 50): Promise<ServiceResponse<PerformanceAchievement[]>> {
    try {
      const { data, error } = await this.supabase
        .from('performance_achievements')
        .select(`
          *,
          courses!inner(name)
        `)
        .eq('user_id', userId)
        .order('achieved_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      // Transform data to include course name
      const transformedData: PerformanceAchievement[] = data?.map(achievement => ({
        ...achievement,
        course_name: achievement.courses?.name
      })) || [];

      return this.handleResponse(transformedData);
    } catch (error) {
      return this.handleResponse(null, error);
    }
  }

  // Check for new records after a round is completed
  async updateRecordsAfterRound(roundData: RoundData): Promise<ServiceResponse<{
    newHoleRecords: HoleRecord[];
    newCourseRecords: CourseRecord[];
    notifications: RecordNotification[];
  }>> {
    try {
      const newHoleRecords: HoleRecord[] = [];
      const newCourseRecords: CourseRecord[] = [];
      const notifications: RecordNotification[] = [];

      // Get user's gender for proper record categorization
      const { data: userProfile } = await this.supabase
        .from('profiles')
        .select('gender, first_name, last_name, nickname')
        .eq('id', roundData.user_id)
        .single();

      const userGender = userProfile?.gender || 'overall';
      const userName = userProfile?.nickname || 
                      `${userProfile?.first_name || ''} ${userProfile?.last_name || ''}`.trim() ||
                      'Unknown Player';

      // Check for course record (total score)
      const courseRecordResult = await this.checkCourseRecord(roundData, userGender);
      if (courseRecordResult.isNewRecord && courseRecordResult.record) {
        newCourseRecords.push(courseRecordResult.record);
        notifications.push({
          type: 'new_record',
          title: `New ${this.getRecordTitle(userGender, 'course')}!`,
          message: `${userName} set a new course record with ${roundData.total_score}`,
          achievement_data: {
            course_name: roundData.course_name,
            score: roundData.total_score,
            previous_holder: courseRecordResult.previousHolder
          },
          timestamp: new Date().toISOString()
        });
      }

      // Check for hole records (individual hole scores)
      if (roundData.hole_scores) {
        for (const holeScore of roundData.hole_scores) {
          const holeRecordResult = await this.checkHoleRecord(
            roundData, 
            holeScore, 
            userGender
          );
          if (holeRecordResult.isNewRecord && holeRecordResult.record) {
            newHoleRecords.push(holeRecordResult.record);
            notifications.push({
              type: 'new_record',
              title: `New ${this.getRecordTitle(userGender, 'hole')}!`,
              message: `${userName} scored ${holeScore.strokes} on Hole ${holeScore.hole_number}`,
              achievement_data: {
                course_name: roundData.course_name,
                hole_number: holeScore.hole_number,
                score: holeScore.strokes,
                previous_holder: holeRecordResult.previousHolder
              },
              timestamp: new Date().toISOString()
            });
          }
        }
      }

      return this.handleResponse({
        newHoleRecords,
        newCourseRecords,
        notifications
      });
    } catch (error) {
      return this.handleResponse(null, error);
    }
  }

  // Private helper method to check for course record
  private async checkCourseRecord(roundData: RoundData, userGender: string) {
    // Check current record for this course/gender/type combination
    const { data: currentRecord } = await this.supabase
      .from('course_records')
      .select(`
        *,
        profiles!inner(first_name, last_name, nickname)
      `)
      .eq('course_id', roundData.course_id)
      .eq('gender', userGender)
      .eq('record_type', 'scratch') // Start with scratch scoring
      .single();

    const isNewRecord = !currentRecord || roundData.total_score < currentRecord.best_total_score;
    let newRecord = null;
    let previousHolder = null;

    if (isNewRecord) {
      if (currentRecord) {
        previousHolder = currentRecord.profiles?.nickname || 
                        `${currentRecord.profiles?.first_name || ''} ${currentRecord.profiles?.last_name || ''}`.trim();
      }

      // Insert/update the record — conflict on the unique (course_id, gender, record_type) constraint
      const { data: insertedRecord, error } = await this.supabase
        .from('course_records')
        .upsert({
          course_id: roundData.course_id,
          gender: userGender,
          record_type: 'scratch',
          best_total_score: roundData.total_score,
          par_differential: roundData.par_differential,
          user_id: roundData.user_id,
          round_id: roundData.id,
          achieved_at: roundData.completed_at,
          total_par: roundData.total_par
        }, { onConflict: 'course_id,gender,record_type' })
        .select()
        .single();

      if (!error && insertedRecord) {
        newRecord = insertedRecord as CourseRecord;
        newRecord.course_name = roundData.course_name;
      }
    }

    return { isNewRecord, record: newRecord, previousHolder };
  }

  // Private helper method to check for hole record
  private async checkHoleRecord(roundData: RoundData, holeScore: { hole_number: number; strokes: number; par: number }, userGender: string) {
    // Check current record for this hole/gender/type combination
    const { data: currentRecord } = await this.supabase
      .from('hole_records')
      .select(`
        *,
        profiles!inner(first_name, last_name, nickname)
      `)
      .eq('course_id', roundData.course_id)
      .eq('hole_number', holeScore.hole_number)
      .eq('gender', userGender)
      .eq('record_type', 'scratch')
      .single();

    const isNewRecord = !currentRecord || holeScore.strokes < currentRecord.best_score;
    let newRecord = null;
    let previousHolder = null;

    if (isNewRecord) {
      if (currentRecord) {
        previousHolder = currentRecord.profiles?.nickname || 
                        `${currentRecord.profiles?.first_name || ''} ${currentRecord.profiles?.last_name || ''}`.trim();
      }

      // Insert/update the record — conflict on the unique (course_id, hole_number, gender, record_type) constraint
      const { data: insertedRecord, error } = await this.supabase
        .from('hole_records')
        .upsert({
          course_id: roundData.course_id,
          hole_number: holeScore.hole_number,
          gender: userGender,
          record_type: 'scratch',
          best_score: holeScore.strokes,
          user_id: roundData.user_id,
          round_id: roundData.id,
          achieved_at: roundData.completed_at,
          par: holeScore.par
        }, { onConflict: 'course_id,hole_number,gender,record_type' })
        .select()
        .single();

      if (!error && insertedRecord) {
        newRecord = insertedRecord as HoleRecord;
        newRecord.course_name = roundData.course_name;
      }
    }

    return { isNewRecord, record: newRecord, previousHolder };
  }

  // Helper method to get record title based on gender
  private getRecordTitle(gender: string, type: 'hole' | 'course'): string {
    if (type === 'hole') {
      switch (gender) {
        case 'male': return 'King of Hole (KOH)';
        case 'female': return 'Queen of Hole (QOH)';
        default: return 'Hole Record (HR)';
      }
    } else {
      switch (gender) {
        case 'male': return 'King of Course (KOC)';
        case 'female': return 'Queen of Course (QOC)';
        default: return 'Course Record (CR)';
      }
    }
  }

  // Get a specific hole record for a course/hole combination
  async getHoleRecordByCourse(
    courseId: string, 
    holeNumber: number, 
    gender: 'male' | 'female' | 'overall' = 'overall',
    recordType: 'scratch' | 'net' = 'scratch'
  ): Promise<ServiceResponse<HoleRecord | null>> {
    try {
      const { data, error } = await this.supabase
        .from('hole_records')
        .select(`
          *,
          courses!inner(name),
          profiles!inner(first_name, last_name, nickname)
        `)
        .eq('course_id', courseId)
        .eq('hole_number', holeNumber)
        .eq('gender', gender)
        .eq('record_type', recordType)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows found
        throw error;
      }

      const transformedData = data ? {
        ...data,
        course_name: data.courses?.name,
        user_first_name: data.profiles?.first_name,
        user_last_name: data.profiles?.last_name,
        user_nickname: data.profiles?.nickname,
        user_name: data.profiles?.nickname || 
                  `${data.profiles?.first_name || ''} ${data.profiles?.last_name || ''}`.trim()
      } : null;

      return this.handleResponse(transformedData);
    } catch (error) {
      return this.handleResponse(null, error);
    }
  }

  // Get a specific course record
  async getCourseRecordByCourse(
    courseId: string,
    gender: 'male' | 'female' | 'overall' = 'overall',
    recordType: 'scratch' | 'net' = 'scratch'
  ): Promise<ServiceResponse<CourseRecord | null>> {
    try {
      const { data, error } = await this.supabase
        .from('course_records')
        .select(`
          *,
          courses!inner(name),
          profiles!inner(first_name, last_name, nickname)
        `)
        .eq('course_id', courseId)
        .eq('gender', gender)
        .eq('record_type', recordType)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows found
        throw error;
      }

      const transformedData = data ? {
        ...data,
        course_name: data.courses?.name,
        user_first_name: data.profiles?.first_name,
        user_last_name: data.profiles?.last_name,
        user_nickname: data.profiles?.nickname,
        user_name: data.profiles?.nickname || 
                  `${data.profiles?.first_name || ''} ${data.profiles?.last_name || ''}`.trim()
      } : null;

      return this.handleResponse(transformedData);
    } catch (error) {
      return this.handleResponse(null, error);
    }
  }
}